/**
 * Scroll-snap carousel for the Testimonials section.
 *
 * The track is a real overflow-x element, so touch swipe, trackpad and keyboard
 * scrolling all work without any JS. This adds the arrows, mouse dragging and
 * the optional autoplay on top.
 */
class TestimonialsSlider extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-testimonials-track]');
    if (!this.track) return;

    this.prevButton = this.querySelector('[data-testimonials-prev]');
    this.nextButton = this.querySelector('[data-testimonials-next]');
    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.interval = parseInt(this.dataset.autoplayInterval, 10) || 0;
    this.paused = false;
    this.timer = null;

    this.prevButton?.addEventListener('click', () => this.step(-1));
    this.nextButton?.addEventListener('click', () => this.step(1));

    this.track.addEventListener('scroll', () => this.updateButtons(), { passive: true });
    this.updateButtons();

    // Autoplay is a nudge, not a hijack: any sign the reader is engaged stops it.
    ['pointerenter', 'focusin', 'pointerdown'].forEach((event) =>
      this.addEventListener(event, () => this.pause())
    );
    ['pointerleave', 'focusout'].forEach((event) =>
      this.addEventListener(event, () => this.resume())
    );
    document.addEventListener('visibilitychange', () =>
      document.hidden ? this.stopTimer() : this.resume()
    );

    this.addDragging();
    this.addQuoteToggles();
    this.resume();
  }

  disconnectedCallback() {
    this.stopTimer();
    window.removeEventListener('resize', this.onResize);
  }

  /** Width of one card plus the gap between cards. */
  get stepDistance() {
    const card = this.track.querySelector('li');
    if (!card) return this.track.clientWidth;
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    return card.getBoundingClientRect().width + gap;
  }

  get atStart() {
    return this.track.scrollLeft <= 1;
  }

  get atEnd() {
    return this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 1;
  }

  step(direction) {
    const behavior = this.reduceMotion.matches ? 'auto' : 'smooth';
    this.track.scrollBy({ left: this.stepDistance * direction, behavior });
  }

  advance() {
    // Wrapping back to the first card keeps the loop going without cloning
    // slides, which would double the review markup for screen readers.
    if (this.atEnd) {
      const behavior = this.reduceMotion.matches ? 'auto' : 'smooth';
      this.track.scrollTo({ left: 0, behavior });
    } else {
      this.step(1);
    }
  }

  updateButtons() {
    this.prevButton?.toggleAttribute('disabled', this.atStart);
    this.nextButton?.toggleAttribute('disabled', this.atEnd);
  }

  startTimer() {
    if (this.timer || !this.interval || this.reduceMotion.matches) return;
    this.timer = setInterval(() => this.advance(), this.interval);
  }

  stopTimer() {
    clearInterval(this.timer);
    this.timer = null;
  }

  pause() {
    this.paused = true;
    this.stopTimer();
  }

  resume() {
    this.paused = false;
    if (!document.hidden) this.startTimer();
  }

  /**
   * The quote is clamped in CSS to --quotes-quote-lines. A review only needs a
   * Read more link when it has more than that, which cannot be known from the
   * text alone: it depends on the card width and on the font in use. So each
   * quote is measured, and the button next to it is unhidden only if the text
   * really is cut off.
   */
  addQuoteToggles() {
    this.toggles = Array.from(this.querySelectorAll('[data-testimonials-toggle]'));
    if (!this.toggles.length) return;

    this.toggles.forEach((toggle) => {
      const quote = document.getElementById(toggle.getAttribute('aria-controls'));
      if (!quote) return;
      toggle.addEventListener('click', () => this.toggleQuote(toggle, quote));
    });

    this.syncToggles();

    // The first measurement runs against whatever font is painted at the time;
    // the real one is usually wider or narrower, which changes the line count.
    document.fonts?.ready.then(() => this.syncToggles());

    // Card widths are fluid, so a resize can add or remove a line.
    this.onResize = () => {
      cancelAnimationFrame(this.resizeFrame);
      this.resizeFrame = requestAnimationFrame(() => this.syncToggles());
    };
    window.addEventListener('resize', this.onResize);
  }

  syncToggles() {
    this.toggles.forEach((toggle) => {
      const quote = document.getElementById(toggle.getAttribute('aria-controls'));
      // An open quote is not clipped, so there is nothing to measure — its
      // button has to stay put or the reader loses the way back.
      if (!quote || toggle.getAttribute('aria-expanded') === 'true') return;
      // The extra pixel absorbs the sub-pixel rounding of a fractional line height.
      toggle.hidden = quote.scrollHeight <= quote.clientHeight + 1;
    });
  }

  toggleQuote(toggle, quote) {
    const expanded = toggle.getAttribute('aria-expanded') === 'true';
    toggle.setAttribute('aria-expanded', String(!expanded));
    quote.classList.toggle('testimonials__quote--expanded', !expanded);
    toggle.textContent = expanded ? toggle.dataset.labelMore : toggle.dataset.labelLess;
  }

  addDragging() {
    let startX = null;
    let startScroll = 0;
    let dragged = false;

    const onDown = (event) => {
      if (event.pointerType === 'touch') return; // native scrolling already handles it
      startX = event.clientX;
      startScroll = this.track.scrollLeft;
      dragged = false;
      this.track.classList.add('is-dragging');
    };

    const onMove = (event) => {
      if (startX === null) return;
      const delta = event.clientX - startX;
      if (Math.abs(delta) > 4) dragged = true;
      this.track.scrollLeft = startScroll - delta;
    };

    const onUp = () => {
      if (startX === null) return;
      startX = null;
      this.track.classList.remove('is-dragging');
    };

    this.track.addEventListener('pointerdown', onDown);
    window.addEventListener('pointermove', onMove, { passive: true });
    window.addEventListener('pointerup', onUp);
    // Swallow the click that ends a drag so it does not follow a card's link or
    // trip the Read more button. preventDefault alone would not stop the latter,
    // which acts on the click event rather than on a default action.
    this.track.addEventListener(
      'click',
      (event) => {
        if (!dragged) return;
        event.preventDefault();
        event.stopPropagation();
      },
      true
    );
    this.track.addEventListener('dragstart', (event) => event.preventDefault());
  }
}

customElements.define('testimonials-slider', TestimonialsSlider);
